import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";

const initialState = {
	list: [],
	total: 0,
	myResignation: null,
	loading: false,
	error: null,
};

// Build payload for employee/EmployeeResignationSetup
// Add: ResignationId = 0. Edit: ResignationId = record id, pass existing fields + ManagerApproval/HRApproval
const buildSetupPayload = (values, isEdit = false) => {
	const payload = {
		ResignationId: isEdit ? values.ResignationId : 0,
		EmployeeId: values.EmployeeId,
		ManagerId: values.ManagerId ?? null,
		HRId: values.HRId ?? null,
		NoticePeriodStartDate: values.NoticePeriodStartDate || null,
		ResignationReason: values.ResignationReason || "",
		NoticePeriod: values.NoticePeriod ?? null,
		Remarks: values.Remarks ?? null,
		LastWorkingDay: values.LastWorkingDay || null,
		ManagerApproval: values.ManagerApproval ?? 0,
		HRApproval: values.HRApproval ?? 0,
	};
	return payload;
};

// Submit resignation (employee) - insert with ResignationId 0
// values: { EmployeeId, NoticePeriodStartDate, ResignationReason, NoticePeriod, Remarks, LastWorkingDay } (or camelCase)
export const submitResignation = createAsyncThunk(
	"resignation/submitResignation",
	async (values, { rejectWithValue }) => {
		try {
			const normalized = {
				ResignationId: 0,
				EmployeeId: values.EmployeeId ?? values.employeeId,
				ManagerId: values.ManagerId ?? values.managerId ?? null,
				HRId: values.HRId ?? values.hrId ?? null,
				NoticePeriodStartDate: values.NoticePeriodStartDate ?? values.noticePeriodStartDate ?? null,
				ResignationReason: values.ResignationReason ?? values.resignationReason ?? values.reason ?? "",
				NoticePeriod: values.NoticePeriod ?? values.noticePeriod ?? values.noticePeriodDays ?? null,
				Remarks: values.Remarks ?? values.remarks ?? null,
				LastWorkingDay: values.LastWorkingDay ?? values.lastWorkingDay ?? values.lastWorkingDate ?? null,
				ManagerApproval: 0,
				HRApproval: 0,
			};
			const payload = buildSetupPayload(normalized, false);
			const { data } = await axios({
				method: "post",
				headers: {
					Accept: "application/json",
					"Content-Type": "application/json",
				},
				url: "employee/EmployeeResignationSetup",
				data: payload,
			});
			toast.success("Resignation submitted successfully");
			return { data, message: "success" };
		} catch (error) {
			const msg =
				error.response?.data?.message ||
				error.response?.data?.error ||
				"Failed to submit resignation";
			toast.error(msg);
			return rejectWithValue(error.response?.data || { message: msg });
		}
	}
);

// Get current user's resignation - employee/GetEmployeeResignationList?EmployeeId=X
export const getMyResignation = createAsyncThunk(
	"resignation/getMyResignation",
	async (employeeId, { rejectWithValue }) => {
		try {
			if (employeeId == null || employeeId === "") {
				return { list: [], myResignation: null };
			}
			const { data } = await axios.get(
				`employee/GetEmployeeResignationList?EmployeeId=${encodeURIComponent(employeeId)}`
			);
			const list = Array.isArray(data) ? data : (Array.isArray(data?.list) ? data.list : []);
			const single = data && !Array.isArray(data) && !data.list ? data : null;
			const mostRecent = list.length > 0 ? list[0] : single;
			return { list, myResignation: mostRecent };
		} catch (error) {
			if (error.response?.status === 404) return { list: [], myResignation: null };
			return rejectWithValue(error.response?.data);
		}
	}
);

// Get list for HR/Manager - employee/GetEmployeeResignationList
export const getResignationList = createAsyncThunk(
	"resignation/getResignationList",
	async (
		{ page = 1, limit = 10, status } = {},
		{ rejectWithValue }
	) => {
		try {
			const params = new URLSearchParams();
			if (page) params.append("page", page);
			if (limit) params.append("limit", limit);
			if (status) params.append("status", status);

			const { data } = await axios.get(
				`employee/GetEmployeeResignationList${params.toString() ? `?${params.toString()}` : ""}`
			);
			return data;
		} catch (error) {
			return rejectWithValue(error.response?.data || {});
		}
	}
);

// Get pending resignations for notifications
export const getPendingResignationsForNotification = createAsyncThunk(
	"resignation/getPendingForNotification",
	async (_, { rejectWithValue }) => {
		try {
			const { data } = await axios.get(
				"employee/GetEmployeeResignationList?page=1&limit=20&status=PENDING"
			);
			const list = Array.isArray(data?.list) ? data.list : Array.isArray(data) ? data : [];
			return list.filter(
				(item) =>
					(String(item?.overallStatus || item?.status || item?.Status || "").toUpperCase() === "PENDING") ||
					(item?.ManagerApproval ?? item?.managerApproval) === 0 ||
					(item?.HRApproval ?? item?.hrApproval) === 0
			);
		} catch (error) {
			return [];
		}
	}
);

// Approve or Reject - Manager: only pass ManagerApproval (1 = approve, 2 = reject). HR: pass HRId (logged-in id) and HRApproval (1 = approve, 2 = reject).
export const reviewResignation = createAsyncThunk(
	"resignation/reviewResignation",
	async (
		{ id, record, approvalFor, approved, reviewerEmployeeId },
		{ rejectWithValue }
	) => {
		try {
			const rid = id ?? record?.ResignationId ?? record?.resignationId ?? record?.id;
			const val = approved ? 1 : 2;
			// Manager: only ManagerApproval. HR: HRId + HRApproval. Admin: both ManagerApproval & HRApproval + HRId = admin id
			const managerApproval =
				approvalFor === "manager" || approvalFor === "admin"
					? val
					: (record?.ManagerApproval ?? record?.managerApproval ?? 0);
			const hrApproval =
				approvalFor === "hr" || approvalFor === "admin"
					? val
					: (record?.HRApproval ?? record?.hrApproval ?? 0);
			const managerId = record?.ManagerId ?? record?.managerId ?? null;
			const hrId =
				(approvalFor === "hr" || approvalFor === "admin") && reviewerEmployeeId != null
					? reviewerEmployeeId
					: (record?.HRId ?? record?.hrId ?? null);

			const payload = buildSetupPayload({
				ResignationId: rid,
				EmployeeId: record?.EmployeeId ?? record?.employeeId,
				ManagerId: managerId,
				HRId: hrId,
				NoticePeriodStartDate: record?.NoticePeriodStartDate ?? record?.noticePeriodStartDate ?? null,
				ResignationReason: record?.ResignationReason ?? record?.ResignationReason ?? record?.resignationReason ?? "",
				NoticePeriod: record?.NoticePeriod ?? record?.noticePeriod ?? null,
				Remarks: record?.Remarks ?? record?.remarks ?? null,
				LastWorkingDay: record?.LastWorkingDay ?? record?.lastWorkingDay ?? record?.LastWorkingDate ?? record?.lastWorkingDate ?? null,
				ManagerApproval: managerApproval,
				HRApproval: hrApproval,
			}, true);

			const { data } = await axios({
				method: "post",
				headers: {
					Accept: "application/json",
					"Content-Type": "application/json",
				},
				url: "employee/EmployeeResignationSetup",
				data: payload,
			});
			toast.success(
				approved ? "Resignation approved" : "Resignation rejected"
			);
			return { data, message: "success" };
		} catch (error) {
			const msg =
				error.response?.data?.message ||
				"Failed to update resignation";
			toast.error(msg);
			return rejectWithValue(error.response?.data || { message: msg });
		}
	}
);

const resignationSlice = createSlice({
	name: "resignation",
	initialState,
	reducers: {
		clearMyResignation: (state) => {
			state.myResignation = null;
		},
		clearResignationError: (state) => {
			state.error = null;
		},
	},
	extraReducers: (builder) => {
		// Submit
		builder.addCase(submitResignation.pending, (state) => {
			state.loading = true;
			state.error = null;
		});
		builder.addCase(submitResignation.fulfilled, (state, action) => {
			state.loading = false;
			if (action.payload?.data) {
				state.myResignation = action.payload.data;
			}
		});
		builder.addCase(submitResignation.rejected, (state, action) => {
			state.loading = false;
			state.error = action.payload?.message || action.error?.message;
		});

		// Get my resignation
		builder.addCase(getMyResignation.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(getMyResignation.fulfilled, (state, action) => {
			state.loading = false;
			const payload = action.payload;
			// If payload has the expected property (even if null), use it.
			if (payload && typeof payload === "object" && "myResignation" in payload) {
				state.myResignation = payload.myResignation;
			} else {
				// Fallback for legacy/direct object return
				state.myResignation = Array.isArray(payload) ? payload[0] : (payload || null);
			}
		});
		builder.addCase(getMyResignation.rejected, (state) => {
			state.loading = false;
			state.myResignation = null;
		});

		// Get list (HR/Manager)
		builder.addCase(getResignationList.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(getResignationList.fulfilled, (state, action) => {
			state.loading = false;
			const payload = action.payload;
			const raw = payload?.list ?? payload?.data ?? payload;
			state.list = Array.isArray(raw) ? raw : [];
			state.total = payload?.total ?? payload?.Total ?? state.list.length;
		});
		builder.addCase(getResignationList.rejected, (state) => {
			state.loading = false;
			state.list = [];
		});

		// Review (approve/reject)
		builder.addCase(reviewResignation.fulfilled, (state, action) => {
			const updated = action.payload?.data;
			if (updated && state.list?.length) {
				const idx = state.list.findIndex(
					(r) =>
						(r.id || r.ResignationId || r.resignationId) ===
						(updated.id || updated.ResignationId || updated.resignationId)
				);
				if (idx !== -1) state.list[idx] = { ...state.list[idx], ...updated };
			}
		});
	},
});

export const { clearMyResignation, clearResignationError } =
	resignationSlice.actions;
export default resignationSlice.reducer;
